import { useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Flame, AlertTriangle, ExternalLink, Check, X } from 'lucide-react';
import { AgGridReact } from 'ag-grid-react';
import {
  type ColDef,
  type ICellRendererParams,
  type SortChangedEvent,
} from 'ag-grid-community';
import Chip, { getBudgetStatusVariant } from '../Chip';
import { BudgetItem, BUDGET_POSITIONS } from '../../types/budget';
import { getPositionColorClasses } from '../../utils/positionColors';
import { ensureAgGridModulesRegistered } from '../shared/BillTable';
import { useTheme } from '@shared/theme/ThemeContext';
import { billTableLightTheme, billTableDarkTheme } from '../shared/ag-grid-theme';
import GridScrollChevrons, { useGridScrollChevrons } from '../shared/GridScrollChevrons';

ensureAgGridModulesRegistered();

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null || isNaN(amount)) return '—';
  if (amount === 0) return '$0';
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(0)}K`;
  return `${sign}$${abs.toLocaleString()}`;
};

const formatRelativeDate = (dateStr: string | undefined): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
};

interface BudgetTableProps {
  items: BudgetItem[];
  context: 'budget' | 'workspace';
  workspaceId?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  onBookmark?: (item: BudgetItem) => void;
  onScrollNearEnd?: () => void;
  canEdit?: boolean;
  onToggleHot?: (itemId: string, isHot: boolean) => void;
  onUpdatePosition?: (itemId: string, position: string) => void;
}

const BudgetTable: React.FC<BudgetTableProps> = ({
  items,
  context,
  workspaceId,
  onSort,
}) => {
  const navigate = useNavigate();
  const gridRef = useRef<AgGridReact>(null);
  const { theme } = useTheme();

  const getItemNumber = (item: BudgetItem) => item.itemNumber || item.item_number;
  const getItemTitle = (item: BudgetItem) => item.itemTitle || item.item_title;
  const getDeptName = (item: BudgetItem) => item.deptName || item.dept_name;
  const getIsHot = (item: BudgetItem) => item.isHot ?? item.tracked_is_hot ?? false;
  const getPosition = (item: BudgetItem) => item.position || item.tracked_position;
  const getReview = (item: BudgetItem) => item.needsClientReview ?? item.tracked_review ?? false;

  const ItemCellRenderer = useCallback((params: ICellRendererParams) => {
    const item = params.data as BudgetItem;
    if (!item) return null;
    const hot = getIsHot(item);
    const review = getReview(item);
    return (
      <div className="flex flex-col gap-1 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-primary-700 dark:text-primary-400">
            {getItemNumber(item)}
          </span>
          {hot && <Flame className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />}
          {review && <AlertTriangle className="w-3.5 h-3.5 text-warning-500 flex-shrink-0" />}
        </div>
        <span className="text-xs text-text-secondary dark:text-text-dark-secondary line-clamp-2">
          {getItemTitle(item)}
        </span>
      </div>
    );
  }, []);

  const StatusCellRenderer = useCallback((params: ICellRendererParams) => {
    const item = params.data as BudgetItem;
    if (!item) return null;
    return <Chip label={item.status.charAt(0).toUpperCase() + item.status.slice(1)} variant={getBudgetStatusVariant(item.status)} size="sm" />;
  }, []);

  const PositionCellRenderer = useCallback((params: ICellRendererParams) => {
    const item = params.data as BudgetItem;
    if (!item) return null;
    const position = getPosition(item);
    if (!position) return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>;
    const label = BUDGET_POSITIONS.find((p) => p.value === position)?.label || position;
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getPositionColorClasses(position)}`}>
        {label}
      </span>
    );
  }, []);

  const LegislationCellRenderer = useCallback((params: ICellRendererParams) => {
    const item = params.data as BudgetItem;
    if (!item) return null;
    return item.requires_legislation
      ? <Check className="w-4 h-4 text-success-600 dark:text-success-400" />
      : <X className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />;
  }, []);

  const FinancialsCellRenderer = useCallback((params: ICellRendererParams) => {
    const item = params.data as BudgetItem;
    if (!item) return null;
    const total = item.total_amount;
    const detail = item.financials_detail;
    if (total == null && !detail?.length) {
      return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>;
    }
    return (
      <div className="group relative">
        <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary cursor-default">
          {formatCurrency(total)}
        </span>
        {detail && detail.length > 0 && (
          <div className="hidden group-hover:block absolute z-50 left-0 top-full mt-1 bg-white dark:bg-background-dark-secondary border border-border-light dark:border-border-dark rounded-lg shadow-lg p-3 min-w-[160px]">
            {detail.filter(d => d.fund_type !== 'Total').map((d, i) => (
              <div key={i} className="flex justify-between text-xs py-0.5">
                <span className="text-text-secondary dark:text-text-dark-secondary">{d.fund_type}</span>
                <span className="text-text-primary dark:text-text-dark-primary font-medium ml-4">
                  {formatCurrency(d.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }, []);

  const LinkedBillsCellRenderer = useCallback((params: ICellRendererParams) => {
    const item = params.data as BudgetItem;
    if (!item?.linked_bills?.length) {
      return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>;
    }
    return (
      <div className="flex flex-wrap gap-1 py-1">
        {item.linked_bills.map((bill) => (
          <button
            key={bill.bill_id}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/legislation/${bill.bill_id}`);
            }}
            className="text-xs px-1.5 py-0.5 rounded bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-colors font-medium"
            title={bill.title}
          >
            {bill.number}
          </button>
        ))}
      </div>
    );
  }, [navigate]);

  const SourceCellRenderer = useCallback((params: ICellRendererParams) => {
    const item = params.data as BudgetItem;
    if (!item?.source_file_url) {
      return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>;
    }
    return (
      <a
        href={item.source_file_url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
        title="Open source document"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Source
      </a>
    );
  }, []);

  const TagsCellRenderer = useCallback((params: ICellRendererParams) => {
    const item = params.data as BudgetItem;
    if (!item?.tags?.length) {
      return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>;
    }
    return (
      <div className="flex flex-wrap gap-1 py-1">
        {item.tags.map((tag, i) => (
          <span
            key={i}
            className="text-xs px-1.5 py-0.5 rounded bg-background-tertiary dark:bg-background-dark-tertiary text-text-secondary dark:text-text-dark-secondary"
          >
            {tag}
          </span>
        ))}
      </div>
    );
  }, []);

  const columnDefs = useMemo<ColDef[]>(() => {
    const cols: ColDef[] = [];

    cols.push({
      colId: 'itemNumber',
      headerName: 'ITEM',
      pinned: 'left',
      width: 260,
      minWidth: 200,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      autoHeight: true,
      wrapText: true,
      cellRenderer: ItemCellRenderer,
      valueGetter: (params: any) => params.data?.item_number || params.data?.itemNumber || '',
    });

    cols.push({
      colId: 'dept_name',
      headerName: 'DEPARTMENT',
      width: 200,
      minWidth: 150,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      valueGetter: (params: any) => getDeptName(params.data) || '',
    });

    cols.push({
      colId: 'status',
      headerName: 'STATUS',
      width: 130,
      minWidth: 100,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      cellRenderer: StatusCellRenderer,
      valueGetter: (params: any) => params.data?.status || '',
    });

    if (context === 'workspace') {
      cols.push({
        colId: 'position',
        headerName: 'POSITION',
        width: 130,
        minWidth: 100,
        sortable: false,
        headerClass: 'ag-bill-table-header-uppercase',
        cellRenderer: PositionCellRenderer,
      });
    }

    cols.push({
      colId: 'total_amount',
      headerName: 'FINANCIALS',
      width: 140,
      minWidth: 110,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      cellRenderer: FinancialsCellRenderer,
      valueGetter: (params: any) => params.data?.total_amount ?? 0,
    });

    cols.push({
      colId: 'requires_legislation',
      headerName: 'REQ. LEG.',
      width: 100,
      minWidth: 80,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      cellRenderer: LegislationCellRenderer,
      valueGetter: (params: any) => params.data?.requires_legislation ? 'Yes' : 'No',
    });

    cols.push({
      colId: 'governor_request_key',
      headerName: 'GOV. REQUEST',
      width: 130,
      minWidth: 100,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      valueGetter: (params: any) => params.data?.governor_request_key || '',
      cellRenderer: (params: ICellRendererParams) => {
        const val = params.data?.governor_request_key;
        if (!val) return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>;
        return <span className="text-xs text-text-primary dark:text-text-dark-primary">{val}</span>;
      },
    });

    cols.push({
      colId: 'linked_bills',
      headerName: 'LINKED BILLS',
      width: 160,
      minWidth: 120,
      sortable: false,
      headerClass: 'ag-bill-table-header-uppercase',
      autoHeight: true,
      cellRenderer: LinkedBillsCellRenderer,
    });

    if (context === 'workspace') {
      cols.push({
        colId: 'tags',
        headerName: 'TAGS',
        width: 160,
        minWidth: 120,
        sortable: false,
        headerClass: 'ag-bill-table-header-uppercase',
        autoHeight: true,
        cellRenderer: TagsCellRenderer,
      });
    }

    cols.push({
      colId: 'source_file_url',
      headerName: 'SOURCE',
      width: 90,
      minWidth: 70,
      sortable: false,
      headerClass: 'ag-bill-table-header-uppercase',
      cellRenderer: SourceCellRenderer,
    });

    cols.push({
      colId: 'updated_at',
      headerName: 'UPDATED',
      width: 110,
      minWidth: 90,
      sortable: true,
      headerClass: 'ag-bill-table-header-uppercase',
      valueGetter: (params: any) => params.data?.updated_at || '',
      cellRenderer: (params: ICellRendererParams) => {
        const item = params.data as BudgetItem;
        if (!item?.updated_at) return <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">—</span>;
        return (
          <span className="text-xs text-text-secondary dark:text-text-dark-secondary" title={new Date(item.updated_at).toLocaleString()}>
            {formatRelativeDate(item.updated_at)}
          </span>
        );
      },
    });

    return cols;
  }, [context, ItemCellRenderer, StatusCellRenderer, PositionCellRenderer, FinancialsCellRenderer, LegislationCellRenderer, LinkedBillsCellRenderer, SourceCellRenderer, TagsCellRenderer]);

  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    if (!onSort) return;
    const colState = event.api.getColumnState();
    const sorted = colState.find((c) => c.sort);
    if (sorted) {
      onSort(sorted.colId || 'item_number', sorted.sort as 'asc' | 'desc');
    }
  }, [onSort]);

  const handleRowClicked = useCallback((event: any) => {
    if (!event.data) return;
    const params = workspaceId ? `?workspace=${workspaceId}` : '';
    navigate(`/budget/${event.data.id}${params}`);
  }, [navigate, workspaceId]);

  const agTheme = theme === 'dark' ? billTableDarkTheme : billTableLightTheme;

  const { wrapperRef, ...chevrons } = useGridScrollChevrons();

  return (
    <div
      ref={wrapperRef}
      className="ag-theme-alpine w-full relative"
      style={{ height: Math.min(items.length * 56 + 48, 600) }}
    >
      <AgGridReact
        ref={gridRef}
        theme={agTheme}
        rowData={items}
        columnDefs={columnDefs}
        defaultColDef={{
          resizable: true,
          suppressMovable: true,
        }}
        rowHeight={56}
        headerHeight={40}
        animateRows={true}
        suppressCellFocus={true}
        onSortChanged={handleSortChanged}
        onRowClicked={handleRowClicked}
        domLayout="normal"
      />

      <GridScrollChevrons {...chevrons} headerHeight={40} />
    </div>
  );
};

export default BudgetTable;
